import React from 'react';
import { Box } from '@mui/material';
import { Link as JoyLink, Typography } from '@mui/joy';
import { Link as RouterLink, To } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ContactChannelLinks from '../ContactChannelLinks';
import ContactNameRow from '../ContactNameRow';

export interface HeroContact {
    id: string;
    name: string;
    role?: string | null;
    email?: string | null;
    phone?: string | null;
}

interface Props {
    contacts: HeroContact[];
    /** Contacts beyond `contacts`, summarised as "+ N more contacts". */
    hiddenCount?: number;
    /** Where the full list lives — turns the summary into a link. */
    moreTo?: To | null;
    /** testid of each contact entry. */
    contactTestId?: string;
}

/**
 * Contact list of the hero side cards, in the installed base contact cards'
 * typography (bodyBold name, soft role chip, body-sm grey channel links) but
 * without the card border.
 */
const HeroContactList = ({ contacts, hiddenCount = 0, moreTo, contactTestId }: Props) => {
    const { t } = useTranslation();
    const moreLabel = t('+ {{count}} more contacts', { count: hiddenCount });

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
            {contacts.map((contact) => (
                <Box key={contact.id} sx={{ minWidth: 0 }} data-testid={contactTestId}>
                    <ContactNameRow name={contact.name} role={contact.role} />
                    <ContactChannelLinks email={contact.email} phone={contact.phone} level="body-sm" sx={{ mt: '6px' }} />
                </Box>
            ))}
            {hiddenCount > 0 && (moreTo ? (
                <JoyLink component={RouterLink} to={moreTo} level="bodySmall" sx={{ width: 'fit-content' }} data-testid="hero-contacts-more">
                    {moreLabel}
                </JoyLink>
            ) : (
                <Typography level="bodySmall" sx={{ color: '#777' }}>
                    {moreLabel}
                </Typography>
            ))}
        </Box>
    );
};

export default HeroContactList;
